import { clsx, type ClassValue } from 'clsx';
import { format, parseISO } from 'date-fns';
import { createElement } from 'react';
import type { ReactNode } from 'react';
import {
  Home,
  Car,
  Utensils,
  Film,
  ShoppingBag,
  HeartPulse,
  Zap,
  GraduationCap,
  PiggyBank,
  Circle,
  Briefcase,
  Plane,
  Dumbbell,
  Music,
  Phone,
  Wifi,
  Gift,
  Coffee,
  BookOpen,
  Heart,
  Shield,
  Gamepad2,
  Shirt,
  PawPrint,
  Baby,
  Wrench,
  Landmark,
  TrendingUp,
  CreditCard,
  Repeat,
  type LucideIcon,
} from 'lucide-react';
import type { Transaction, Category, Budget, DashboardSummary, User } from './types';

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(date: string): string {
  try {
    return format(parseISO(date), 'MMM d, yyyy');
  } catch {
    return date;
  }
}

export function formatMonth(month: number, year: number): string {
  const date = new Date(year, month - 1, 1);
  return format(date, 'MMMM yyyy');
}

export function cn(...classes: ClassValue[]): string {
  return clsx(classes);
}

const categoryEmojiMap: Record<string, string> = {
  food: '\uD83C\uDF54',
  groceries: '\uD83D\uDED2',
  dining: '\uD83C\uDF7D\uFE0F',
  restaurants: '\uD83C\uDF7D\uFE0F',
  transport: '\uD83D\uDE97',
  transportation: '\uD83D\uDE97',
  housing: '\uD83C\uDFE0',
  rent: '\uD83C\uDFE0',
  mortgage: '\uD83C\uDFE0',
  utilities: '\u26A1',
  electric: '\u26A1',
  water: '\uD83D\uDCA7',
  internet: '\uD83C\uDF10',
  entertainment: '\uD83C\uDFAC',
  shopping: '\uD83D\uDECD\uFE0F',
  clothing: '\uD83D\uDC55',
  health: '\uD83C\uDFE5',
  healthcare: '\uD83C\uDFE5',
  medical: '\uD83C\uDFE5',
  education: '\uD83D\uDCDA',
  insurance: '\uD83D\uDEE1\uFE0F',
  savings: '\uD83D\uDCB0',
  investment: '\uD83D\uDCC8',
  salary: '\uD83D\uDCB5',
  income: '\uD83D\uDCB5',
  freelance: '\uD83D\uDCBB',
  gifts: '\uD83C\uDF81',
  travel: '\u2708\uFE0F',
  vacation: '\uD83C\uDFD6\uFE0F',
  fitness: '\uD83C\uDFCB\uFE0F',
  gym: '\uD83C\uDFCB\uFE0F',
  pets: '\uD83D\uDC3E',
  subscriptions: '\uD83D\uDD04',
  phone: '\uD83D\uDCF1',
  personal: '\uD83D\uDC64',
  other: '\uD83D\uDCCC',
};

export function getCategoryIcon(name: string): string {
  const key = name.toLowerCase().trim();
  return categoryEmojiMap[key] || '\uD83D\uDCCC';
}

// Map of Lucide icon name strings (as stored in the DB) to actual Lucide components
const lucideIconMap: Record<string, LucideIcon> = {
  'home': Home,
  'car': Car,
  'utensils': Utensils,
  'film': Film,
  'shopping-bag': ShoppingBag,
  'heart-pulse': HeartPulse,
  'zap': Zap,
  'graduation-cap': GraduationCap,
  'piggy-bank': PiggyBank,
  'circle': Circle,
  'briefcase': Briefcase,
  'plane': Plane,
  'dumbbell': Dumbbell,
  'music': Music,
  'phone': Phone,
  'wifi': Wifi,
  'gift': Gift,
  'coffee': Coffee,
  'book-open': BookOpen,
  'heart': Heart,
  'shield': Shield,
  'gamepad-2': Gamepad2,
  'shirt': Shirt,
  'paw-print': PawPrint,
  'baby': Baby,
  'wrench': Wrench,
  'landmark': Landmark,
  'trending-up': TrendingUp,
  'credit-card': CreditCard,
  'repeat': Repeat,
};

/**
 * Resolves a category icon value to a renderable React node.
 * - If the icon is a Lucide icon name string (e.g. "home", "car"), render the Lucide component
 * - If the icon is already an emoji (contains non-ASCII), render it as-is
 * - Otherwise, fall back to getCategoryIcon() based on the category name
 */
export function resolveCategoryIcon(icon: string, categoryName: string, color?: string): ReactNode {
  // If empty, use emoji fallback
  if (!icon) {
    return getCategoryIcon(categoryName);
  }

  // Check if this is a known Lucide icon name
  const LucideComponent = lucideIconMap[icon.toLowerCase().trim()];
  if (LucideComponent) {
    return createElement(LucideComponent, {
      size: 20,
      style: color ? { color } : undefined,
    });
  }

  // Check if it looks like an emoji (contains characters outside basic ASCII)
  // eslint-disable-next-line no-control-regex
  const isEmoji = /[^\x00-\x7F]/.test(icon);
  if (isEmoji) {
    return icon;
  }

  // Fallback: try to match icon string as a category name, otherwise use category name
  return getCategoryIcon(icon) !== '\uD83D\uDCCC' ? getCategoryIcon(icon) : getCategoryIcon(categoryName);
}

export function getPercentage(spent: number, budget: number): number {
  if (budget <= 0) return 0;
  return Math.round((spent / budget) * 100);
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Normalize snake_case API responses to camelCase for frontend consumption
export function normalizeUser(u: Record<string, unknown>): User {
  return {
    id: u.id as string,
    email: u.email as string,
    name: u.name as string,
    plan: (u.plan as User['plan']) || 'free',
    createdAt: (u.createdAt || u.created_at || '') as string,
  };
}

export function normalizeTransaction(t: Record<string, unknown>): Transaction {
  return {
    id: t.id as string,
    userId: (t.userId || t.user_id || '') as string,
    amount: t.amount as number,
    type: t.type as 'income' | 'expense',
    categoryId: (t.categoryId || t.category_id || '') as string,
    categoryName: (t.categoryName || t.category_name || '') as string,
    categoryColor: (t.categoryColor || t.category_color || '') as string,
    description: (t.description || '') as string,
    date: t.date as string,
    createdAt: (t.createdAt || t.created_at || '') as string,
    isRecurring: !!(t.isRecurring || t.is_recurring),
    recurrenceInterval: (t.recurrenceInterval || t.recurrence_interval || undefined) as Transaction['recurrenceInterval'],
  };
}

export function normalizeCategory(c: Record<string, unknown>): Category {
  return {
    id: c.id as string,
    userId: (c.userId || c.user_id || '') as string,
    name: c.name as string,
    color: c.color as string,
    icon: (c.icon || '') as string,
    isDefault: !!(c.isDefault || c.is_default),
  };
}

export function normalizeBudget(b: Record<string, unknown>): Budget {
  return {
    id: b.id as string,
    userId: (b.userId || b.user_id || '') as string,
    categoryId: (b.categoryId || b.category_id || '') as string,
    categoryName: (b.categoryName || b.category_name || '') as string,
    categoryColor: (b.categoryColor || b.category_color || '') as string,
    amount: b.amount as number,
    spent: (b.spent as number) ?? 0,
    month: b.month as number,
    year: b.year as number,
  };
}

export function normalizeDashboardSummary(d: Record<string, unknown>): DashboardSummary {
  const raw = d as Record<string, unknown>;
  const monthlyRaw = (raw.monthlyBreakdown || []) as Record<string, unknown>[];
  const catRaw = (raw.categorySpending || []) as Record<string, unknown>[];
  const recentRaw = (raw.recentTransactions || []) as Record<string, unknown>[];
  const budgetRaw = (raw.budgetProgress || []) as Record<string, unknown>[];

  return {
    totalIncome: (raw.totalIncome as number) || 0,
    totalExpenses: (raw.totalExpenses as number) || 0,
    balance: (raw.balance as number) || 0,
    monthlyBreakdown: monthlyRaw.map((m) => ({
      month: formatMonthLabel(m.month as number, m.year as number),
      income: (m.income as number) || 0,
      expense: ((m.expenses ?? m.expense) as number) || 0,
    })),
    categorySpending: catRaw.map((c) => ({
      categoryId: (c.categoryId || c.category_id || '') as string,
      categoryName: (c.categoryName || c.category_name || 'Uncategorized') as string,
      categoryColor: (c.categoryColor || c.category_color || '#6366f1') as string,
      spent: ((c.spent ?? c.amount) as number) || 0,
    })),
    recentTransactions: recentRaw.map((t) => normalizeTransaction(t)),
    budgetProgress: budgetRaw.map((b) => ({
      ...normalizeBudget(b),
      spent: (b.spent as number) ?? 0,
    })),
  };
}

function formatMonthLabel(month: number, year: number): string {
  const date = new Date(year, month - 1, 1);
  return format(date, 'MMM');
}
